import React, { useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import Select from "react-select";
import { MdOutlineDeleteOutline } from "react-icons/md";

const PieVisualization = ({
    indicators,
    locations,
    onSubmit, // Handler from parent
    // Receive all states as props from parent (NO MORE useState!)
    localSelectedLocations,
    setLocalSelectedLocations,
    pie_chartType,
    setPie_chartType,
    pieIndicators,
    setPieIndicators
}) => {
    // Redux selector for pie config (for restoration only)
    const pieConfig = useSelector((state) => state.mapConfig.pieConfig);

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State / UT";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    const noOptionsMessage = () => "No compatible indicators found in your workspace.";

    useEffect(() => {
        if (!locations || locations.length === 0) return;

        const validOptions = [
            {
                label: `All States / UTs of India`,
                value: 1000, geo_level: 1, type: "Children"
            },
            ...locations.map(f => ({
                value: f.properties?.code,
                label: `${f.name || f.properties?.name} (${geoLevelLabel(f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4)})`,
                geo_level: f.geo_level_code || f.geo_level || f.properties?.geo_level_code || 4,
                type: ""
            }))
        ];

        // Filter out any selected locations that no longer exist
        const filteredSelected = localSelectedLocations.filter(selected =>
            validOptions.some(opt => opt.value === selected.value && opt.type === selected.type)
        );

        if (filteredSelected.length !== localSelectedLocations.length) {
            setLocalSelectedLocations(filteredSelected);
        }
    }, [locations]);

    // Handle pie indicator change
    const handlePieIndicatorChange = (index, selectedOption) => {
        setPieIndicators((prev) => {
            const newIndicators = [...prev];
            if (!newIndicators[index]) {
                newIndicators[index] = { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null };
            }
            newIndicators[index].colorIndicator = selectedOption;
            newIndicators[index].colorSubIndicators = selectedOption.subIndicators || [];
            newIndicators[index].selectedColorSubIndicator = null;
            return newIndicators;
        });
    };

    // Handle sub indicator change
    const handleSubIndicatorChange = (index, selectedOption) => {
        setPieIndicators((prev) => {
            const newIndicators = [...prev];
            newIndicators[index].selectedColorSubIndicator = selectedOption;
            return newIndicators;
        });
    };

    // Add indicator
    const handleAddPieIndicator = () => {
        setPieIndicators(prev => {
            if (prev.length >= 4) {
                return prev;
            }
            const next = [
                ...prev,
                { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null },
            ];
            return next;
        });
    };

    // Remove indicator
    const handleRemovePieIndicator = (idx) => {
        setPieIndicators(prev => {
            const next = prev.filter((_, i) => i !== idx);
            return next;
        });
    };

    // Handle reset
    const handleReset = () => {
        setLocalSelectedLocations([]);
        setPie_chartType('pie');
        setPieIndicators([
            { colorIndicator: null, colorSubIndicators: [], selectedColorSubIndicator: null }
        ]);
    };

    // Data patch/restoration logic using useEffect to read from Redux pieConfig
    useEffect(() => {
        if (!pieConfig) return;
        if (pieConfig?.indicators?.length > 0) {
            if (pieConfig.selectedLocations) {
                setLocalSelectedLocations(pieConfig.selectedLocations);
            }
        }
        if (pieConfig.setpie_chartType) {
            setPie_chartType(pieConfig.setpie_chartType)
        }

        const patchedIndicators = pieConfig.indicators.map((ind) => {
            const matched = indicators.find((f) => f.indicator_code === ind);
            return {
                colorIndicator: matched
                    ? {
                        value: matched.indicator_code,
                        label: matched.indicator_name,
                        subIndicators: matched.sub_indicators || [],
                    }
                    : { value: ind.i_code, label: ind.i_code },
            };
        });

        setPieIndicators(patchedIndicators);
    }, [pieConfig, indicators]);

    // Submit functionality - NO API CALL, just prepare data and call parent handler
    const handlePieSubmit = () => {
        const lgdDatas = localSelectedLocations.map(item => ({
            code: item.value,
            geo_level_code: item.geo_level,
        }));

        const formattedIndicators = pieIndicators.map(
            (ind) => ind.colorIndicator?.value || ""
        );

        const payload = {
            lgd_codes: lgdDatas,
            indicators: formattedIndicators,
        };

        const formattedPayload = {
            chartType: pie_chartType === "pie" || pie_chartType === "donut" ? pie_chartType : "pie",
            location_info: localSelectedLocations,
            indicator_info: indicators.reduce((acc, cur) => {
                acc[cur.indicator_code] = cur;
                return acc;
            }, {}),
        };

        const reduxPayload = {
            visualizationType: "pie",
            selectedLocations: localSelectedLocations,
            indicators: formattedIndicators,
            setpie_chartType: pie_chartType
        };

        // Call parent submit handler with prepared data
        onSubmit({ payload, formattedPayload, reduxPayload });
    };

    // Calculate max indicators based on location count
    const maxIndicators = localSelectedLocations.length === 1
        ? 4
        : localSelectedLocations.length === 2
            ? 2
            : 1;

    // Trim indicators if current length exceeds max
    useEffect(() => {
        if (pieIndicators.length > maxIndicators) {
            setPieIndicators(pieIndicators.slice(0, maxIndicators));
        }
    }, [maxIndicators, pieIndicators.length]);
    // Filter pie indicators that are no longer available in the current workspace
    useEffect(() => {
        if (!pieIndicators || pieIndicators.length === 0) return;
        if (!indicators || indicators.length === 0) return;

        // Collect all valid indicator codes from current workspace
        const availableIndicatorCodes = new Set(indicators.map(i => i.indicator_code));

        // Keep indicators that are either empty or still valid
        const filteredIndicators = pieIndicators.filter(ind => {
            if (!ind.colorIndicator) return true; // keep if user hasn't selected anything yet
            return availableIndicatorCodes.has(ind.colorIndicator.value);
        });

        // Only update state if something changed
        if (filteredIndicators.length !== pieIndicators.length) {
            setPieIndicators(filteredIndicators);
        }
    }, [indicators, pieIndicators]);
    

    return (
        <div className="col-md-12 mt-3">
            <div className="cardbg p-3">
                <Form.Label>Pie Chart Configuration</Form.Label>
                <Form.Group className="mb-3">
                    <Form.Label>Chart Type</Form.Label>
                    <div>
                        <Form.Check
                            inline
                            type="radio"
                            label="Pie"
                            name="pieChartType"
                            value="pie"
                            checked={pie_chartType === "pie"}
                            onChange={(e) => setPie_chartType(e.target.value)}
                        />
                        <Form.Check
                            inline
                            type="radio"
                            label="Doughnut"
                            name="pieChartType"
                            value="donut"
                            checked={pie_chartType === "donut"}
                            onChange={(e) => setPie_chartType(e.target.value)}
                        />
                    </div>
                </Form.Group>
                <div className="col-md-8 text-start"><p style={{ 'font-size': '0.85em', 'color': '#2c7ce5' }}> Maximum of four location - indicator combinations can be selected to view upto 4 pie charts in the visulization</p></div>

                <div className="col-md-6">
                    <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Select
                            isMulti
                            options={[
                                {
                                    label: "India (National)",
                                    value: 1000,
                                    geo_level: 1,
                                },
                                ...locations.map((f) => ({
                                    value: f.properties?.code || f.id,
                                    label: `${f.name || f.properties?.name} (${geoLevelLabel(f.properties?.geo_level_code || f.geo_level_code || f.geo_level || 4)})`,
                                    geo_level: f.properties?.geo_level_code || f.geo_level_code || f.geo_level,
                                    type: "",
                                })),
                            ]}
                            onChange={setLocalSelectedLocations}
                            value={localSelectedLocations}
                            placeholder="Choose Location"
                            getOptionValue={(e) => `${e.value}-${e.type}`}
                            getOptionLabel={(e) => e.label}
                        />
                    </Form.Group>
                </div>

                {/* Indicator list */}
                {pieIndicators.map((ind, idx) => (
                    <div key={idx} className="mb-3 p-3 border rounded">
                        <Form.Label>Indicator {idx + 1}</Form.Label>
                        <div className="row align-items-center">
                            <div className="col-md-5">
                                <Form.Group>
                                    <Select
                                        options={indicators.filter(
                                            (f) => f.measurement_type !== "Categorical - Nominal - Text" && Array.isArray(f.sub_indicators) && f.sub_indicators.length > 0 && f.sub_indicators.some(sub => sub.name && sub.name !== 'NA')
                                        )
                                            .map(f => ({
                                                value: f.indicator_code,
                                                label: `${f.indicator_name} (${f.indicator_date})`,
                                            }))}
                                        onChange={(opt) => handlePieIndicatorChange(idx, opt)}
                                        value={ind.colorIndicator}
                                        classNamePrefix="select"
                                        placeholder="Choose Indicator"
                                        noOptionsMessage={noOptionsMessage}
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-2 d-flex justify-content-end ">
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleRemovePieIndicator(idx)}
                                >
                                    <MdOutlineDeleteOutline />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Add button (disabled after max indicators) */}
                <Button
                    variant="outline-primary"
                    onClick={handleAddPieIndicator}
                    disabled={pieIndicators.length >= maxIndicators}
                >
                    + Add Indicator
                </Button>

                <div className="row mt-2">
                    <div className="col-md-8 text-start"><p style={{ 'font-size': '0.85em' }}> <i>Note: </i>Indicators added to the workspace are listed in the dropdown list. To include additional indicators, go to the Home page and add the required indicators to the workspace.</p></div>
                    <div className="text-end col-md-4 gap-2">
                        <Button variant="secondary" onClick={handleReset} className="btn-sm">
                            Reset
                        </Button>&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button
                            onClick={handlePieSubmit}
                            className="btn-sm btn-primary"
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PieVisualization;